import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowUp, Calendar, CheckCircle2, Circle, Loader2, XCircle } from "lucide-react";
import { format } from "date-fns";
import { useRoadmapVoting } from "@/hooks/useRoadmapVoting";
import { trackRoadmapVote } from "@/lib/analytics";
import { useState } from "react";

interface RoadmapCardProps {
  id: string;
  title: string;
  description: string;
  status: "planned" | "in_progress" | "completed" | "cancelled";
  category: string;
  quarter: string;
  priority: "low" | "medium" | "high";
  votes: number;
  expectedDate?: string;
  completedDate?: string;
}

const statusConfig = {
  planned: { label: "Planned", color: "bg-blue-500/10 text-blue-500 border-blue-500/20", icon: Circle },
  in_progress: { label: "In Progress", color: "bg-orange-500/10 text-orange-500 border-orange-500/20", icon: Loader2 },
  completed: { label: "Completed", color: "bg-green-500/10 text-green-500 border-green-500/20", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", color: "bg-muted text-muted-foreground border-muted", icon: XCircle },
};

const priorityConfig = {
  low: "text-muted-foreground",
  medium: "text-orange-500",
  high: "text-red-500",
};

export const RoadmapCard = ({
  id,
  title,
  description,
  status,
  category,
  quarter,
  priority,
  votes: initialVotes,
  expectedDate,
  completedDate,
}: RoadmapCardProps) => {
  const { hasVoted, vote } = useRoadmapVoting();
  const [votes, setVotes] = useState(initialVotes);
  const [isVoting, setIsVoting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const voted = hasVoted(id);

  const statusInfo = statusConfig[status];
  const StatusIcon = statusInfo.icon;

  const handleVote = async () => {
    if (voted || isVoting) return;
    
    setIsVoting(true);
    const success = await vote(id, votes);
    if (success) {
      setVotes(votes + 1);
      trackRoadmapVote(title, 'upvote');
    }
    setIsVoting(false);
  };

  return (
    <>
      <Card 
        className="h-full hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-xl mb-2">{title}</CardTitle>
              <div className="flex flex-wrap gap-2 mb-2">
                <Badge variant="outline" className={statusInfo.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusInfo.label}
                </Badge>
                <Badge variant="secondary">{category}</Badge>
                <Badge variant="outline">{quarter}</Badge>
              </div>
            </div>
            <Button
              size="sm"
              variant={voted ? "secondary" : "outline"}
              onClick={(e) => {
                e.stopPropagation();
                handleVote();
              }}
              disabled={voted || isVoting}
              className="flex-shrink-0"
            >
              <ArrowUp className={`w-4 h-4 mr-1 ${voted ? "fill-current" : ""}`} />
              {votes}
            </Button>
          </div>
          <CardDescription className="mt-2">
            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${priorityConfig[priority]}`} style={{ backgroundColor: "currentColor" }} />
            {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4 line-clamp-3">{description}</p>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {expectedDate && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Expected: {format(new Date(expectedDate), "MMM d, yyyy")}</span>
              </div>
            )}
            {completedDate && (
              <div className="flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                <span>Completed: {format(new Date(completedDate), "MMM d, yyyy")}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{title}</DialogTitle>
            <DialogDescription className="sr-only">
              Detailed view of {title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className={statusInfo.color}>
                <StatusIcon className="w-4 h-4 mr-1" />
                {statusInfo.label}
              </Badge>
              <Badge variant="secondary">{category}</Badge>
              <Badge variant="outline">{quarter}</Badge>
              <Badge variant="outline">
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${priorityConfig[priority]}`} style={{ backgroundColor: "currentColor" }} />
                {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority
              </Badge>
            </div>

            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-muted-foreground">{description}</p>
            </div>

            <div className="flex flex-wrap gap-6">
              {expectedDate && (
                <div>
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Expected Date
                  </h3>
                  <p className="text-muted-foreground">{format(new Date(expectedDate), "MMMM d, yyyy")}</p>
                </div>
              )}
              {completedDate && (
                <div>
                  <h3 className="font-semibold mb-1 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Completed Date
                  </h3>
                  <p className="text-muted-foreground">{format(new Date(completedDate), "MMMM d, yyyy")}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <ArrowUp className={`w-5 h-5 ${voted ? "fill-current" : ""}`} />
                <span className="font-semibold">{votes} votes</span>
              </div>
              <Button
                variant={voted ? "secondary" : "default"}
                onClick={handleVote}
                disabled={voted || isVoting}
              >
                {voted ? "Voted" : "Vote for this"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
